import React, { useCallback, useEffect, useRef, useState } from "react";
import Reveal from "../Reveal";
import SectionIntro from "./SectionIntro";
import { VALUES } from "../../data/about";

/**
 * Five beliefs as a list you move through, not five cards you compare.
 *
 * A row at rest is a number and a word. Under the pointer it takes the ink,
 * slides a little, draws its rule and opens its description — so the reader
 * uncovers one value at a time instead of being handed a wall of five. A
 * panel follows the pointer down the list carrying whichever value is live,
 * which is what stops a plain list of words from reading as a table of
 * contents.
 *
 * Everything hover-driven is a fine-pointer enhancement. On touch there is no
 * hover to give, so every description is simply open and the follower never
 * mounts.
 */

function ValuesList() {
  const followerRef = useRef(null);
  const frame = useRef(0);
  const next = useRef(null);

  const [pointer, setPointer] = useState(false);
  const [active, setActive] = useState(null);

  useEffect(() => {
    const query = window.matchMedia(
      "(hover: hover) and (pointer: fine) and (min-width: 1024px)"
    );
    const still = window.matchMedia("(prefers-reduced-motion: reduce)");

    const check = () => setPointer(query.matches && !still.matches);

    check();
    query.addEventListener("change", check);
    still.addEventListener("change", check);

    return () => {
      query.removeEventListener("change", check);
      still.removeEventListener("change", check);
    };
  }, []);

  /* The follower is positioned by transform and written once per frame.
     pointermove fires far more often than the screen refreshes. */
  const flush = useCallback(() => {
    frame.current = 0;
    const node = followerRef.current;
    if (!node || !next.current) return;
    node.style.setProperty("--mw-fx", `${next.current.x}px`);
    node.style.setProperty("--mw-fy", `${next.current.y}px`);
  }, []);

  const onPointerMove = useCallback(
    (event) => {
      if (!pointer) return;
      next.current = { x: event.clientX, y: event.clientY };
      if (!frame.current) frame.current = requestAnimationFrame(flush);
    },
    [pointer, flush]
  );

  useEffect(
    () => () => {
      if (frame.current) cancelAnimationFrame(frame.current);
    },
    []
  );

  const live = active === null ? null : VALUES.items[active];

  return (
    <section
      id="values"
      className="relative scroll-mt-24 bg-[#FAF8F2] py-24 lg:py-32"
      onPointerMove={onPointerMove}
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <Reveal>
          <SectionIntro index={VALUES.index} label={VALUES.label} title={VALUES.title} />
        </Reveal>

        <div className="mt-12 lg:mt-16" onMouseLeave={() => setActive(null)}>
          {VALUES.items.map((item, i) => (
            <Reveal key={item.n} delay={i * 70}>
              <div
                className="mw-row group relative border-t border-[#1E1B16]/12 last:border-b"
                onMouseEnter={() => setActive(i)}
                onFocus={() => setActive(i)}
                onBlur={() => setActive(null)}
                tabIndex={0}
              >
                {/* The rule that draws itself along the top of the live row. */}
                <span
                  className="mw-rule absolute -top-px left-0 h-px w-full bg-[#6A0DAD]"
                  aria-hidden="true"
                />

                <div className="flex flex-col gap-5 py-8 transition-transform duration-700 [transition-timing-function:var(--mw-glide)] lg:flex-row lg:items-baseline lg:gap-12 lg:py-10 lg:group-hover:translate-x-4 lg:group-focus-within:translate-x-4">
                  {/* Outlined until the row is live, then filled — the number
                      is what tracks progress through the list. The stroke is
                      cleared on hover so the filled state is clean rather
                      than filled-and-outlined. */}
                  <span
                    className="mw-stroke shrink-0 pt-2 text-2xl font-bold tracking-[0.18em] transition-colors duration-500 group-hover:text-[#6A0DAD] group-hover:[-webkit-text-stroke:0px] group-focus-within:text-[#6A0DAD] group-focus-within:[-webkit-text-stroke:0px] lg:w-20"
                    style={{ "--mw-stroke-color": "rgba(30,27,22,0.35)" }}
                  >
                    {item.n}
                  </span>

                  {/* Sized just under .mw-h2: large enough to carry the row,
                      short enough that five of them plus a description still
                      sit comfortably on a laptop screen. */}
                  <h3 className="shrink-0 text-[clamp(1.7rem,3.2vw,2.6rem)] font-bold leading-[1.05] tracking-[-0.03em] text-[#1E1B16]/35 transition-colors duration-500 group-hover:text-[#1E1B16] group-focus-within:text-[#1E1B16] lg:w-[30%]">
                    {item.title}
                  </h3>

                  {/* Closed at rest on a pointer device, always open on touch:
                      grid-template-rows animates between 0fr and 1fr without
                      needing a measured height. */}
                  <div className="grid flex-1 grid-rows-[1fr] transition-[grid-template-rows] duration-700 [transition-timing-function:var(--mw-glide)] lg:grid-rows-[0fr] lg:opacity-0 lg:transition-[grid-template-rows,opacity] lg:group-hover:grid-rows-[1fr] lg:group-hover:opacity-100 lg:group-focus-within:grid-rows-[1fr] lg:group-focus-within:opacity-100">
                    <div className="overflow-hidden">
                      <p className="mw-lede max-w-lg text-[#4D4B46]">{item.body}</p>
                    </div>
                  </div>

                  <span className="mw-eyebrow shrink-0 text-[#6A0DAD] lg:pt-2">
                    {item.tag}
                  </span>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      {/* --------------------------- the follower ---------------------------
          Fixed to the viewport and moved by transform only, so it never
          participates in layout and never makes the list reflow. */}
      {pointer ? (
        <div
          ref={followerRef}
          className={`mw-follow z-40 hidden lg:block ${live ? "mw-follow-on" : ""}`}
          aria-hidden="true"
        >
          <div className="relative flex h-40 w-40 flex-col justify-between overflow-hidden rounded-2xl bg-[linear-gradient(150deg,#7B1BC4_0%,#4C087D_100%)] p-5 shadow-[0_40px_70px_-30px_rgba(76,8,125,0.9)] ring-1 ring-white/20">
            <span className="mw-grain absolute inset-0 opacity-[0.16] mix-blend-overlay" />
            <span className="relative text-[2.6rem] font-bold leading-none tracking-tight text-white/90">
              {live ? live.n : ""}
            </span>
            <span className="relative text-[13px] font-semibold leading-snug text-[#F4E8FF]">
              {live ? live.tag : ""}
            </span>
          </div>
        </div>
      ) : null}
    </section>
  );
}

export default ValuesList;
